import React, {useEffect, useId, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {AppButton} from '@/components/Button';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import {MaxWidthContainer} from '@/components/Layout';
import PopupSticked from '@/components/Popup/PopupSticked';
import ModalTypes from '@/consts/app/modalTypes';
import usePushNotificationSettingsChange from '@/hooks/pushNotifications/usePushNotificationSettingsChange';
import useIsWelcomeMode from '@/hooks/useIsWelcomeMode';
import amplitudeNotificationTracking from '@/services/analytics/amplitude/amplitudeNotificationTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import appContent from '@/services/content/appContent';
import {hideModalAction} from '@/state/slices/modalSlice';

const useNotificationPromptPopup = () => useMemo(() => appContent.getNotificationPromptPopup() || {}, []);

const EnableNotificationReminderModal = ({promptType}) => {
    const {isWelcomeMode, isAnimationFinished} = useIsWelcomeMode();
    const {title, description, items, icon, button} = useNotificationPromptPopup();
    const {handleNotificationSettingChange} = usePushNotificationSettingsChange({hideFailedMessage: true});
    const dispatch = useDispatch();
    const headerId = useId();

    useEffect(() => {
        googleAnalyticsService.trackPushNotificationsPromptViewed(promptType);
        amplitudeNotificationTracking.trackPushNotificationsPromptViewed(promptType);
    }, []);

    const closeModal = () => {
        dispatch(hideModalAction(ModalTypes.ENABLE_NOTIFICATION_REMINDER));
    };
    const onClick = () => {
        closeModal();
        handleNotificationSettingChange(true);
    };

    const onClose = () => {
        closeModal();
    };

    if (isWelcomeMode && !isAnimationFinished) return null;

    return (
        <PopupSticked
            isCloseVisible
            className='flex flex-col items-center rounded-t-xl'
            closeButtonClass='top-5'
            onClose={onClose}
            aria-labelledby={headerId}
            isBlockerOverlay
        >
            <MaxWidthContainer className='mt-10 flex-1 px-4'>
                <RemoteIconV2 icon={icon} className='mt-8 h-16 w-16' />
                <h1 id={headerId} className='mb-0 mt-4 text-28'>
                    {title}
                </h1>
                {description && <p className='mt-2'>{description}</p>}
                <ul className='mb-8 mt-8 flex w-full flex-col gap-4 rounded-xl bg-primary-dark-5 p-4'>
                    {items?.map((item, i) => {
                        const {title, path} = item;

                        if (!title) return null;

                        return (
                            <li key={i} className='flex items-center justify-start gap-2'>
                                <div className='min-w-8 shrink-0'>
                                    <RemoteIconV2 icon={{path}} />
                                </div>
                                <span className='text-start text-14 font-bold'>{title}</span>
                            </li>
                        );
                    })}
                </ul>
                {button && (
                    <AppButton className='m-auto ml:w-full' primary light aria-label={button.alt} onClick={onClick}>
                        {button.text}
                    </AppButton>
                )}
            </MaxWidthContainer>
        </PopupSticked>
    );
};

export default EnableNotificationReminderModal;
